import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  useTheme,
  useMediaQuery,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  AttachMoney as BillingIcon,
  ExpandLess,
  ExpandMore,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  LocalOffer as PlansIcon,
  Tune as BillingSettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';

const drawerWidth = 280;

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  children?: NavItem[];
}

const navigationItems: NavItem[] = [
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/admin',
  },
  {
    text: 'Users',
    icon: <PeopleIcon />,
    path: '/admin/users',
  },
  {
    text: 'Billing',
    icon: <BillingIcon />,
    path: '/admin/billing',
    children: [
      {
        text: 'Overview',
        icon: <DashboardIcon />,
        path: '/admin/billing',
      },
      {
        text: 'Subscription Plans',
        icon: <PlansIcon />,
        path: '/admin/billing/plans',
      },
      {
        text: 'Credit Packs',
        icon: <CreditCardIcon />,
        path: '/admin/billing/credit-packs',
      },
      {
        text: 'Invoices',
        icon: <ReceiptIcon />,
        path: '/admin/billing/invoices',
      },
      {
        text: 'Settings',
        icon: <BillingSettingsIcon />,
        path: '/admin/billing/settings',
      },
    ],
  },
  {
    text: 'Question Types',
    icon: <CategoryIcon />,
    path: '/admin/question-types',
  },
  {
    text: 'System Settings',
    icon: <SettingsIcon />,
    path: '/admin/settings',
  },
];

export function AdminLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [billingExpanded, setBillingExpanded] = useState(() => {
    return location.pathname.startsWith('/admin/billing');
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleBillingToggle = () => {
    setBillingExpanded(!billingExpanded);
  };

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path: string, hasChildren?: boolean) => {
    if (hasChildren) {
      handleBillingToggle();
    } else {
      navigate(path);
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Header */}
      <Box
        sx={{
          p: 3,
          bgcolor: '#1F2A44',
          color: 'white',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          NLPask Admin
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          System Management
        </Typography>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {navigationItems.map((item) => {
          const active = isActive(item.path);
          const hasChildren = item.children && item.children.length > 0;
          const isBillingExpanded = item.text === 'Billing' && billingExpanded;

          return (
            <Box key={item.text}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path, hasChildren)}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 2,
                    backgroundColor: active && !hasChildren ? 'rgba(43, 196, 173, 0.08)' : 'transparent',
                    color: active && !hasChildren ? '#2BC4AD' : 'text.primary',
                    '&:hover': {
                      backgroundColor: 'rgba(43, 196, 173, 0.12)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: active && !hasChildren ? '#2BC4AD' : 'text.secondary',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: active && !hasChildren ? 600 : 500,
                      fontSize: '0.938rem',
                    }}
                  />
                  {hasChildren && (isBillingExpanded ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>

              {/* Submenu for Billing */}
              {hasChildren && (
                <Collapse in={isBillingExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children?.map((child) => {
                      const childActive = location.pathname === child.path;
                      return (
                        <ListItem key={child.text} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            component={Link}
                            to={child.path}
                            onClick={() => isMobile && setMobileOpen(false)}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              pl: 6,
                              pr: 2,
                              backgroundColor: childActive ? 'rgba(43, 196, 173, 0.08)' : 'transparent',
                              color: childActive ? '#2BC4AD' : 'text.primary',
                              '&:hover': {
                                backgroundColor: 'rgba(43, 196, 173, 0.12)',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: childActive ? '#2BC4AD' : 'text.secondary',
                                minWidth: 36,
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.text}
                              primaryTypographyProps={{
                                fontWeight: childActive ? 600 : 500,
                                fontSize: '0.875rem',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>

      <Divider />

      {/* User Info Footer */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: '#2BC4AD',
              width: 40,
              height: 40,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            {user?.fullName?.charAt(0).toUpperCase() || 'A'}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.fullName || 'Admin'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              Administrator
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar for mobile */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            width: '100%',
            bgcolor: '#1F2A44',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
              NLPask Admin
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
          mt: { xs: 8, md: 0 }, // Add top margin on mobile for AppBar
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default AdminLayout;
